import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  AlertTriangle,
  Loader2,
  Clock,
  Terminal,
  Network,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { ModeToggle } from '@/components/mode-toggle';
import { 
  apiClient, 
  EKSCluster, 
  EKSClusterStep,
  EKSClusterProgressResponse,
  cleanErrorMessage 
} from '../api/client';
import { MobileMenuButton } from '@/components/MobileMenuButton';

export function EKSClusterProgress() {
  const { clusterId } = useParams<{ clusterId: string }>();
  const navigate = useNavigate();
  const [cluster, setCluster] = useState<EKSCluster | null>(null);
  const [steps, setSteps] = useState<EKSClusterStep[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!clusterId) {
      navigate('/cloud-integrations');
      return;
    }

    loadClusterProgress();
    
    // Set up polling for real-time updates
    const interval = setInterval(loadClusterProgress, 5000); // Poll every 5 seconds

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterId, navigate]);

  const loadClusterProgress = async () => {
    if (!clusterId) return;

    try {
      setError('');
      const response: EKSClusterProgressResponse = await apiClient.getEKSClusterProgress(clusterId);
      setCluster(response.cluster);
      setSteps(response.steps);
    } catch (err: any) {
      setError(cleanErrorMessage(err) || 'Failed to load cluster progress');
    } finally {
      setIsLoading(false);
    }
  };

  const getStepIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'in_progress':
        return <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />;
      case 'failed':
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStepDescription = (stepName: string) => {
    switch (stepName) {
      case 'Create VPC and Networking':
        return 'Setting up VPC, subnets, and security groups';
      case 'Create IAM Roles':
        return 'Creating IAM roles for cluster and node groups';
      case 'Create EKS Cluster':
        return 'Provisioning the EKS control plane';
      case 'Create Node Group':
        return 'Creating worker nodes for your applications';
      case 'Install Cilium CNI':
        return 'Installing Cilium for network observability';
      case 'Install Kestrel Operator':
        return 'Installing Kestrel AI monitoring agent';
      case 'Validate Cluster':
        return 'Running final validation checks';
      default:
        return 'Processing...';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ready':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Ready
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return (
          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Creating
          </Badge>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!cluster) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">EKS Cluster</h1>
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>Cluster not found or access denied</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MobileMenuButton />
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Terminal className="h-6 w-6 text-blue-600" />
              {cluster.cluster_name}
            </h1>
            <p className="text-muted-foreground">
              EKS Cluster • {cluster.region} • {cluster.version}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {getStatusBadge(cluster.status)}
          <ModeToggle />
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Cluster Error Display */}
      {cluster.error_message && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Cluster Creation Failed:</strong> {cluster.error_message}
          </AlertDescription>
        </Alert>
      )}

      {/* Progress Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Creation Progress</span>
            <span className="text-sm font-normal text-muted-foreground">
              {cluster.progress_percentage}% Complete
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Progress value={cluster.progress_percentage} className="w-full" />
          {cluster.current_step && (
            <div className="text-sm text-muted-foreground">
              Current: {cluster.current_step}
            </div>
          )}
          
          {cluster.status === 'ready' && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                🎉 Your EKS cluster is ready! You can now deploy applications and monitor them with Kestrel AI.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Detailed Steps */}
      <Card>
        <CardHeader>
          <CardTitle>Creation Steps</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {getStepIcon(step.status)}
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">{step.step_name}</h4>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      {step.started_at && (
                        <span>Started: {formatDate(step.started_at)}</span>
                      )}
                      {step.completed_at && (
                        <span>• Completed: {formatDate(step.completed_at)}</span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {getStepDescription(step.step_name)}
                  </p>
                  {step.error_message && (
                    <Alert variant="destructive" className="mt-2">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{step.error_message}</AlertDescription>
                    </Alert>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Cluster Information */}
      <Card>
        <CardHeader>
          <CardTitle>Cluster Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-muted-foreground">Cluster Name</span>
                <div className="font-mono text-sm">{cluster.cluster_name}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-muted-foreground">Region</span>
                <div>{cluster.region}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-muted-foreground">Kubernetes Version</span>
                <div>{cluster.version}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-muted-foreground">Node Group Size</span>
                <div className="capitalize">{cluster.node_group_size}</div>
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-muted-foreground">Instance Types</span>
                <div className="font-mono text-sm">{cluster.instance_types.join(', ')}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-muted-foreground">Nodes</span>
                <div>{cluster.min_nodes} - {cluster.max_nodes} (desired: {cluster.desired_nodes})</div>
              </div>
              <div>
                <span className="text-sm font-medium text-muted-foreground">Created</span>
                <div>{formatDate(cluster.created_at)}</div>
              </div>
              {cluster.ready_at && (
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Ready At</span>
                  <div>{formatDate(cluster.ready_at)}</div>
                </div>
              )}
            </div>
          </div>

          {/* AWS Resources */}
          {(cluster.cluster_arn || cluster.vpc_id) && (
            <div className="mt-6 pt-6 border-t">
              <h4 className="font-medium mb-3">AWS Resources</h4>
              <div className="space-y-2 text-sm">
                {cluster.cluster_arn && (
                  <div>
                    <span className="font-medium text-muted-foreground">Cluster ARN:</span>
                    <div className="font-mono text-xs mt-1 break-all">{cluster.cluster_arn}</div>
                  </div>
                )}
                {cluster.vpc_id && (
                  <div>
                    <span className="font-medium text-muted-foreground">VPC ID:</span>
                    <div className="font-mono text-xs mt-1">{cluster.vpc_id}</div>
                  </div>
                )}
                {cluster.cluster_endpoint && (
                  <div>
                    <span className="font-medium text-muted-foreground">Cluster Endpoint:</span>
                    <div className="font-mono text-xs mt-1 break-all">{cluster.cluster_endpoint}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Next Steps (when cluster is ready) */}
      {cluster.status === 'ready' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Next Steps
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Cluster is Ready</p>
                  <p className="text-sm text-muted-foreground">
                    Your EKS cluster is now operational with Cilium CNI and Kestrel operator installed.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Terminal className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Connect with kubectl</p>
                  <p className="text-sm text-muted-foreground mb-2">
                    Configure kubectl to access your cluster:
                  </p>
                  <div className="bg-muted p-2 rounded font-mono text-xs">
                    aws eks update-kubeconfig --region {cluster.region} --name {cluster.cluster_name}
                  </div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Network className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Monitor Network Traffic</p>
                  <p className="text-sm text-muted-foreground">
                    Your cluster is now sending resource metadata and network flow data to Kestrel AI.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
